import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery, useAction, useMutation, usePaginatedQuery } from 'convex/react';
import { api } from '../../../../convex/_generated/api';
import type { Id, Doc } from '../../../../convex/_generated/dataModel';
import { TagChip } from '../../../shared/ui/components/TagChip';
import { SimpleTagChip } from '../../../shared/ui/components/SimpleTagChip';
import { NavigationHeader } from '../../search/components/NavigationHeader';
import { useSearch } from '../../../shared/contexts/SearchContext';
import { AIPromptGeneratorModal, type PromptType } from '../../saved-prompts/components/AIPromptGeneratorModal';
import { collectReferenceData } from '../../saved-prompts/components/aiPromptGenerator';
import { applySimpleFilters, hasActiveFilters as hasNewFilters } from '../../search/utils/simpleFilterUtils';
import { toast } from 'sonner';
import { useFullscreenViewer } from '../../../shared/contexts/FullscreenViewerContext';
import type { ViewerMediaItem } from '../../../shared/contexts/FullscreenViewerContext';

type ReferenceDoc = Doc<'references'> & {
  imageUrl?: string | null;
  sourceURL?: string | null;
  tags: Doc<'tags'>[];
  mediaItems: (Doc<'mediaItems'> & {
    url: string | null;
    mediaType: 'image' | 'video';
  })[];
  app?: Doc<'apps'> | null;
};

interface ReferencePageProps {
  onEditReference?: (reference: ReferenceDoc) => void;
}

export function ReferencePage({ onEditReference }: ReferencePageProps) {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [_searchParams] = useSearchParams();
  const { filters, setFilters, filterState, filterDispatch } = useSearch();
  
  // AI Prompt Generator state
  const [isAIPromptModalOpen, setIsAIPromptModalOpen] = useState(false);
  
  // Public sharing state
  const [isTogglingPublic, setIsTogglingPublic] = useState(false);
  const { openViewer } = useFullscreenViewer();
  
  // Convex actions and mutations
  const generateAIPrompt = useAction(api.aiPromptGenerator.generateAIPrompt);
  const updateReferencePrivacy = useMutation(api.publicDirectory.updateReferencePrivacy);
  
  // Fetch the specific reference
  const reference = useQuery(
    api.references.getReferenceDetails,
    id ? { referenceId: id as Id<'references'> } : 'skip'
  );

  // Fetch references using both legacy and new filter systems
  // Legacy system filters
  const hasActiveFilters = filters.searchText || filters.tags.length > 0 || filters.apps.length > 0;
  const searchResults = useQuery(
    api.references.searchReferences,
    hasActiveFilters
      ? {
          searchQuery: filters.searchText,
          tagIds: filters.tags.filter((t: any) => !t.negative).map((t: any) => t.id),
          appIds: filters.apps.filter((a: any) => !a.negative).map((a: any) => a.id),
        }
      : 'skip'
  );

  // New filter system
  const hasNewFilterState = hasNewFilters(filterState);
  const hasSearchTerm = !!filterState.searchTerm?.trim();

  // Advanced filtering: paginated path (no search term)
  const advancedPaginated = usePaginatedQuery(
    api.references.searchReferencesAdvanced,
    (hasNewFilterState && !hasSearchTerm)
      ? {
          searchTerm: '',
          tags: filterState.tags,
          globalLogic: filterState.globalLogic,
        }
      : 'skip',
    { initialNumItems: 40 }
  );

  // Advanced filtering: collected path (with search term)
  const advancedSearchCollected = useQuery(
    api.references.searchReferencesAdvanced,
    (hasNewFilterState && hasSearchTerm)
      ? {
          searchTerm: filterState.searchTerm || '',
          tags: filterState.tags,
          globalLogic: filterState.globalLogic,
        }
      : 'skip'
  );

  // Get all references when no filters are applied (paginated)
  const allReferencesPaginated = usePaginatedQuery(
    api.references.getReferences,
    (!hasActiveFilters && !hasNewFilterState) ? {} : 'skip',
    { initialNumItems: 40 }
  );

  // Determine which results to show - prioritize new filter system
  let references: ReferenceDoc[] = (!hasActiveFilters && !hasNewFilterState)
    ? (allReferencesPaginated.results || []) as ReferenceDoc[]
    : (hasNewFilterState && !hasSearchTerm)
      ? (advancedPaginated.results || []) as ReferenceDoc[]
      : hasNewFilterState
        ? ((advancedSearchCollected as any)?.page || []) as ReferenceDoc[]
        : (searchResults || []) as ReferenceDoc[];

  // Ensure references is always an array
  references = Array.isArray(references) ? references : [];

  // Apply legacy client-side negative filtering if using legacy system
  if (!hasNewFilterState && hasActiveFilters) {
    references = references.filter((ref: ReferenceDoc) => {
      const negativeTags = filters.tags.filter((t: any) => t.negative);
      if (negativeTags.length > 0) {
        const hasNegativeTag = negativeTags.some((negTag: any) => 
          ref.tags.some((tag: Doc<'tags'>) => tag._id === negTag.id)
        );
        if (hasNegativeTag) return false;
      }

      const negativeApps = filters.apps.filter((a: any) => a.negative);
      if (negativeApps.length > 0) {
        const hasNegativeApp = negativeApps.some((negApp: any) => ref.appId === negApp.id);
        if (hasNegativeApp) return false;
      }

      return true;
    });
  }

  // Get current reference index and enable navigation
  const currentIndex = references.findIndex(ref => ref._id === id);
  const totalReferences = references.length;
  const hasPrevious = currentIndex > 0;
  const hasNext = currentIndex < totalReferences - 1;

  // Navigation functions - preserve URL parameters
  const navigatePrevious = useCallback(() => {
    if (hasPrevious) {
      const previousRef = references[currentIndex - 1];
      const currentParams = new URLSearchParams(window.location.search);
      const queryString = currentParams.toString();
      const targetUrl = `/reference/${previousRef._id}${queryString ? `?${queryString}` : ''}`;
      void navigate(targetUrl);
    }
  }, [hasPrevious, references, currentIndex, navigate]);

  const navigateNext = useCallback(() => {
    if (hasNext) {
      const nextRef = references[currentIndex + 1];
      const currentParams = new URLSearchParams(window.location.search);
      const queryString = currentParams.toString();
      const targetUrl = `/reference/${nextRef._id}${queryString ? `?${queryString}` : ''}`;
      void navigate(targetUrl);
    }
  }, [hasNext, references, currentIndex, navigate]);

  // Handle back to search - preserve URL parameters for filter state
  const handleBackToSearch = useCallback(() => {
    // Preserve current URL parameters to maintain filter state
    const currentParams = new URLSearchParams(window.location.search);
    const queryString = currentParams.toString();
    const targetUrl = `/${queryString ? `?${queryString}` : ''}`;
    void navigate(targetUrl);
  }, [navigate]);

  // Convert filters to NavigationHeader format - support both legacy and new systems
  const activeFilters = hasNewFilterState 
    ? [] // NavigationHeader will show new filters automatically via SearchContext
    : [
        ...filters.tags.map((tag: any) => ({
          type: 'tag' as const,
          id: tag.id,
          name: tag.name,
          negative: tag.negative
        })),
        ...filters.apps.map((app: any) => ({
          type: 'app' as const,
          id: app.id,
          name: app.name,
          negative: app.negative
        })),
        ...(filters.searchText ? [{
          type: 'search' as const,
          name: filters.searchText
        }] : [])
      ];

  // Handle filter removal - support both legacy and new systems
  const handleRemoveFilter = useCallback((type: 'tag' | 'app' | 'search', id?: string) => {
    if (hasNewFilterState) {
      // Handle new filter system removal via NavigationHeader interaction
      // The NavigationHeader will handle this directly through filterDispatch
      return;
    }

    // Handle legacy filter system
    if (type === 'tag') {
      setFilters({
        ...filters,
        tags: filters.tags.filter((tag: any) => tag.id !== id)
      });
    } else if (type === 'app') {
      setFilters({
        ...filters,
        apps: filters.apps.filter((app: any) => app.id !== id)
      });
    } else if (type === 'search') {
      setFilters({
        ...filters,
        searchText: ''
      });
    }
  }, [hasNewFilterState, filters, setFilters]);

  // Keyboard event handler
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          navigatePrevious();
          break;
        case 'ArrowRight':
          event.preventDefault();
          navigateNext();
          break;
        case 'Escape':
          event.preventDefault();
          handleBackToSearch();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [navigatePrevious, navigateNext, handleBackToSearch]);

  // Handle tag clicks
  const handleTagClick = (tagId: string) => {
    // Navigate back to search with the tag filter applied
    const allTags = reference?.tags || [];
    const tag = allTags.find((t: Doc<'tags'>) => t._id === tagId);
    if (!tag) return;

    const existingTagIndex = filters.tags.findIndex((t: any) => t.id === tagId);
    if (existingTagIndex === -1) {
      const newRawQuery = filters.rawQuery ? `${filters.rawQuery} tag:${tag.name}` : `tag:${tag.name}`;
      setFilters({
        ...filters,
        tags: [...filters.tags, { id: tagId as Id<'tags'>, name: tag.name }],
        rawQuery: newRawQuery
      });
    }
    void navigate('/');
  };

  // Handle AI prompt generation - now uses real OpenAI integration
  const handleGenerateAIPrompt = async (
    reference: ReferenceDoc, 
    promptType: PromptType, 
    customInstructions?: string
  ): Promise<string> => {
    try {
      // Collect and process reference data
      const referenceData = await collectReferenceData(reference);
      
      // Call the Convex action for OpenAI integration
      const result = await generateAIPrompt({
        referenceData,
        promptType,
        customInstructions
      });

      return result;
    } catch (error) {
      console.error('Error generating AI prompt:', error);
      throw error instanceof Error ? error : new Error('Failed to generate AI prompt');
    }
  };

  // Handle public sharing toggle
  const handleTogglePublic = useCallback(async () => {
    if (!reference || isTogglingPublic) return;
    
    setIsTogglingPublic(true);
    try {
      const newIsPublic = !reference.isPublic;
      await updateReferencePrivacy({
        referenceId: reference._id,
        isPublic: newIsPublic
      });
      
      if (newIsPublic) {
        toast.success('Reference submitted for public review! It will appear in the public directory once approved.');
      } else {
        toast.success('Reference made private successfully.');
      }
    } catch (error) {
      console.error('Failed to update reference privacy:', error);
      toast.error('Failed to update sharing settings. Please try again.');
    } finally {
      setIsTogglingPublic(false);
    }
  }, [reference, isTogglingPublic, updateReferencePrivacy]);

  // Loading state
  if (reference === undefined) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="loading loading-spinner loading-lg text-primary"></div>
          <p className="text-gray-700 mt-4">Loading reference...</p>
        </div>
      </div>
    );
  }

  // Error state - reference not found
  if (reference === null || !id) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="card bg-white shadow-lg border-2 border-gray-300 max-w-md">
          <div className="card-body text-center">
            <h1 className="card-title text-2xl justify-center mb-4 text-gray-900">Reference Not Found</h1>
            <p className="text-gray-700 mb-6">The reference you're looking for doesn't exist or has been deleted.</p>
            <div className="card-actions justify-center">
              <button
                onClick={handleBackToSearch}
                className="btn btn-primary"
              >
                Back to Search
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen">
      {/* Navigation Header */}
      <NavigationHeader
        onBackToSearch={handleBackToSearch}
        currentIndex={currentIndex}
        totalReferences={totalReferences}
        hasPrevious={hasPrevious}
        hasNext={hasNext}
        onNavigatePrevious={navigatePrevious}
        onNavigateNext={navigateNext}
        activeFilters={activeFilters}
        onRemoveFilter={handleRemoveFilter}
      />

      {/* Reference Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="card bg-yellow-300 shadow-lg border-2 border-gray-300">
          <div className="card-body">
            {/* Header */}
            <div className="mb-6">
              <h1 className="card-title text-3xl mb-2 text-gray-900">{reference.title}</h1>
              <div className="flex items-center space-x-4 text-sm text-gray-600">
                <div className="badge badge-secondary badge-lg">
                  {reference.type} {reference.category && `- ${reference.category}`}
                </div>
                {reference.app && (
                  <div className="badge badge-info badge-lg">
                    App: {reference.app.name}
                    {reference.app.url && (
                      <a
                        href={reference.app.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 ml-2"
                      >
                        ({reference.app.url})
                      </a>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Media Content */}
            {reference.mediaItems && reference.mediaItems.length > 0 && (
              <div className="mb-6 space-y-4">
                {reference.mediaItems.map((item: Doc<'mediaItems'> & { url: string | null; mediaType: 'image' | 'video' }, index: number) => (
                  item.url && (
                    <div key={`${reference._id}-media-${index}`} className="relative">
                      {item.mediaType === 'video' ? (
                        <div className="relative">
                          <video
                            src={item.url}
                            className="w-full rounded-lg shadow-lg border-2 border-gray-300"
                            controls
                            preload="metadata"
                            title={`${reference.title} - part ${index + 1}`}
                          >
                            <track kind="captions" />
                          </video>
                          <div className="badge badge-neutral absolute top-4 right-4">
                            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                              <title>Video icon</title>
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            Video
                          </div>
                        </div>
                      ) : (
                        <img
                          src={item.url}
                          alt={`${reference.title} content ${index + 1}`}
                          className="w-full rounded-lg shadow-lg border-2 border-gray-300 cursor-zoom-in"
                          onClick={() => {
                            const items: ViewerMediaItem[] = reference.mediaItems
                              .filter((mi): mi is typeof mi & { url: string } => mi.url !== null)
                              .map((mi) => ({ url: mi.url, mediaType: mi.mediaType }));
                            const viewerIndex = items.findIndex((vi) => vi.url === item.url);
                            openViewer(items, viewerIndex >= 0 ? viewerIndex : 0);
                          }}
                        />
                      )}
                    </div>
                  )
                ))}
              </div>
            )}

            {/* Fallback to legacy imageUrl if no mediaItems */}
            {(reference.mediaItems && reference.mediaItems.length === 0) && reference.imageUrl && (
              <div className="mb-6">
                <img
                  src={reference.imageUrl}
                  alt={reference.title}
                  className="w-full rounded-lg shadow-lg border-2 border-gray-300 cursor-zoom-in"
                  onClick={() => {
                    openViewer([{ url: reference.imageUrl!, mediaType: 'image' }], 0);
                  }}
                />
              </div>
            )}

            {/* Description */}
            {reference.description && (
              <div className="mb-6">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Description</h3>
                <div className="bg-gray-100 p-4 rounded-lg border border-gray-300">
                  <p className="text-gray-800 whitespace-pre-wrap">{reference.description}</p>
                </div>
              </div>
            )}

            {/* Source URL */}
            {reference.sourceURL && (
              <div className="mb-6">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Source</h3>
                <div className="bg-gray-100 p-4 rounded-lg border border-gray-300">
                  <a
                    href={reference.sourceURL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-blue-600 hover:text-blue-800 break-all"
                  >
                    {reference.sourceURL}
                  </a>
                </div>
              </div>
            )}

            {/* Tags */}
            {reference.tags && reference.tags.length > 0 && (
              <div className="mb-6">
                <h3 className="text-lg font-bold text-gray-900 mb-2">Tags</h3>
                <div className="flex flex-wrap gap-2">
                  {reference.tags.map((tag: Doc<'tags'>) => (
                    <TagChip
                      key={tag._id}
                      tag={tag}
                      onClick={handleTagClick}
                      variant="default"
                      size="md"
                      showClickHint={true}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="card-actions justify-end pt-4 border-t border-gray-300">
              <button
                onClick={() => setIsAIPromptModalOpen(true)}
                type="button"
                className="btn btn-secondary"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                Generate AI Prompt
              </button>
              
              {/* Public Sharing Toggle */}
              <button
                onClick={handleTogglePublic}
                disabled={isTogglingPublic}
                type="button"
                className={`btn ${
                  reference.isPublic 
                    ? 'btn-warning' 
                    : 'btn-success'
                } ${isTogglingPublic ? 'loading' : ''}`}
                title={reference.isPublic ? 'Make private' : 'Share with community'}
              >
                {!isTogglingPublic && (
                  <svg className="h-4 w-4 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    {reference.isPublic ? (
                      // Private icon
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                    ) : (
                      // Share/Globe icon  
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0-9v9m0 9c-5 0-9-4-9-9s4-9 9-9" />
                    )}
                  </svg>
                )}
                {isTogglingPublic 
                  ? 'Updating...' 
                  : reference.isPublic 
                    ? 'Make Private' 
                    : 'Share Public'
                }
              </button>
              
              {onEditReference && (
                <button
                  onClick={() => onEditReference(reference)}
                  className="btn btn-primary"
                >
                  Edit Reference
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* AI Prompt Generator Modal */}
      {reference && (
        <AIPromptGeneratorModal
          reference={reference}
          isOpen={isAIPromptModalOpen}
          onClose={() => setIsAIPromptModalOpen(false)}
          onGeneratePrompt={handleGenerateAIPrompt}
        />
      )}
    </div>
  );
} 